import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material'
import { useState } from 'react'

import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import EmailNotifications from '@/components/EmailNotifications'
import EmailNotificationsSkeleton from '@/components/EmailNotifications/skeleton'
import { resendNotificationMutation, getEmailNotificationsQuery } from '@/lib/apollo'

export type SubjectType = string

export interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  subjectType: SubjectType
  onClose: () => void
  testId?: string
}

const sendEmailTitle = (subjectType: SubjectType) => {
  const subjectTypeTitleCase = subjectType.charAt(0).toUpperCase() + subjectType.slice(1)

  return `${subjectTypeTitleCase} Reference Requests`
}

const SendEmail = ({ customerId, subjectId, subjectType, onClose, testId }: Props) => {
  const [selectedEmail, setSelectedEmail] = useState<string | undefined>()
  const [emailSent, setEmailSent] = useState<boolean>(false)
  const { useQuery, toTransformedData } = getEmailNotificationsQuery
  const { useMutation } = resendNotificationMutation
  const {
    data,
    loading,
    error,
    refetch: getEmailNotifications,
  } = useQuery({
    variables: {
      customerId,
      subjectId,
      limit: 40,
      offset: 0,
    },
  })
  const [resendNotification, { loading: mutationLoading, error: mutationError }] =
    useMutation()

  const notifications = toTransformedData(data)

  const handleSendReferenceRequest = (notificationId: string) => {
    resendNotification({
      variables: {
        customerId,
        subjectId,
        notificationId,
      },
    })
      .then(() => {
        getEmailNotifications()
        setEmailSent(true)
        setSelectedEmail(undefined)

        // Adding an additional fetch as in some cases
        // we can't retrieve the most recent notification
        // immediately
        setTimeout(() => getEmailNotifications(), 2000)
      })
      .catch(error => {
        console.log(error)
      })
  }

  return (
    <div data-testid={testId}>
      <SlideInSidebar
        header={
          <Stack direction="column" spacing={2} mb={2}>
            <Typography variant="h5">{sendEmailTitle(subjectType)}</Typography>
            <Typography variant="body1">
              Select the email you would like to send again:
            </Typography>
          </Stack>
        }
        content={
          <Box p={1}>
            {error !== undefined ? (
              <>An unexpected error occurred</>
            ) : notifications === undefined || loading || mutationLoading ? (
              <EmailNotificationsSkeleton />
            ) : (
              <EmailNotifications
                notifications={notifications.results}
                selectedNotification={selectedEmail}
                onSelectNotification={setSelectedEmail}
              />
            )}
          </Box>
        }
        footer={
          <Stack direction="column" pt={3} spacing={2}>
            {mutationError !== undefined && (
              <Box mb={3}>
                <Alert severity="error">Failed to send referencing request email.</Alert>
              </Box>
            )}
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              alignItems="center"
            >
              {(loading || mutationLoading) && (
                <CircularProgress size={18} data-testid="loading-progress" />
              )}
              <Button
                onClick={onClose}
                variant="outlined"
                color="inherit"
                sx={{ lineHeight: 'normal' }}
                size="large"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  if (selectedEmail !== undefined) {
                    handleSendReferenceRequest(selectedEmail)
                  }
                }}
                disabled={
                  mutationLoading ||
                  selectedEmail === undefined ||
                  mutationError !== undefined
                }
                variant="contained"
                sx={{ lineHeight: 'normal' }}
                size="large"
              >
                Send email
              </Button>
            </Stack>
          </Stack>
        }
        onClose={onClose}
      />
      <Snackbar
        open={emailSent}
        autoHideDuration={5000}
        onClose={() => setEmailSent(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Email sent successfully.
        </Alert>
      </Snackbar>
    </div>
  )
}

export default SendEmail
